import { MutableRefObject } from 'react';
import * as PIXI from "pixi.js"

import * as FBRConstants from '../FBRConstants';
import * as FBRTypes from '../FBRTypes';

const getTileId: FBRTypes.GetTileIdFunction = (function() {
	let tileIdSequence: number = 0;
	return () => {
		tileIdSequence++;
		return tileIdSequence;
	}
})();

const scoreChangePixiTextOptions = { 
	fontFamily: 'Odibee Sans', 
	fontSize: 34, 
	fill: 0xffffff, 
	align: 'center' 
}

const createTile = (
	tileTexture: PIXI.Texture,
	bottomCorrectIndicatorIconTexture: PIXI.Texture,
	bottomIncorrectIndicatorIconTexture: PIXI.Texture,
	initialXSpeed: number, 
	initialYSpeed: number,
	maxTileValue: number,
	centerOffsetContainerRef: MutableRefObject<PIXI.Container | undefined>,
	createTextChoiceResult: FBRTypes.CreateTextChoiceResultFunction,
	createIconChoiceResult: FBRTypes.CreateIconChoiceResultFunction,
	createCombinedChoiceResult: FBRTypes.CreateCombinedChoiceResultFunction,
	incrementScore: FBRTypes.IncrementScoreFunction,
	tilesRef: MutableRefObject<FBRTypes.Tiles | undefined>,
	globalState: MutableRefObject<FBRTypes.GlobalState>,
	showNoiceVideoFunc: () => void,
	pixiAppRef: MutableRefObject<PIXI.Application | undefined>,
	indicatorsRef: MutableRefObject<{[key:number]:FBRTypes.ChoiceResultIndicator}>,
) => {
	let id:number = getTileId();
	//console.log(`created new tile with id = ${id}`);
	let x = 0;
	let y = 0;
	let xSpeed = initialXSpeed;
	let ySpeed = initialYSpeed;
	let state = 'initial';
	let tileValue = Math.round(Math.random()*maxTileValue);
	//69420 test path
	/*
	if(Math.random() > 0.5) {
		tileValue = 69;
	} else {
		tileValue = 420;
	}
	*/
	let fizzBuzzDetermination = '';
	if(tileValue % 3 == 0) {
		fizzBuzzDetermination += 'fizz';
	} 
	if(tileValue % 5 == 0) {
		fizzBuzzDetermination += 'buzz';
	}

	//create tile sprite
	let sprite = PIXI.Sprite.from(tileTexture); 
	
	//determine tile starting position
	const tileWidthWithMargin = (sprite.width+FBRConstants.TILE_SPRITE_MARGIN*2);
	const tileSpawnXWidth = FBRConstants.STAGE_WIDTH - FBRConstants.TILE_X_STARTING_POSITION_MARGIN*2;
	const tileSpawnPositions = Math.floor(tileSpawnXWidth/tileWidthWithMargin)
	const tileSpawnPositionIndex = Math.floor(tileSpawnPositions*Math.random())+1;
	const tileXSpawnPosition = (FBRConstants.TILE_X_STARTING_POSITION_MARGIN + (tileSpawnPositionIndex * tileWidthWithMargin) - tileWidthWithMargin/2) - FBRConstants.STAGE_WIDTH/2;
	x = tileXSpawnPosition;
	y = -(FBRConstants.STAGE_HEIGHT/2) - 110;
	
	//initial configuration for sprite
	sprite.name = String(id);
	sprite.tint = Math.random() * 0xFFFFFF;
	sprite.anchor.set(0.5,0.5);
	sprite.x = x;
	sprite.y = y;
	sprite.interactive = true;

	//setup hit area	
	sprite.hitArea = new PIXI.Polygon([
		-19.5, -47.5,
		19.7, -47.5,
		47.5, -19.5,
		47.5, 19.7,
		19.7, 47.5,
		-19.5, 47.5,
		-47.5, 19.7,
		-47.5, -19.5
	]);

	//create value text
	const tileValueText = new PIXI.Text(tileValue, { fontFamily: 'Quantico', fontSize: 30, fill: 0xffffff, align: 'center' });
	tileValueText.anchor.set(0.5, 0.5);
	tileValueText.x = 0;
	tileValueText.y = 0;
	sprite.addChild(tileValueText);
	//add tile to centerOffsetContainer
	if(centerOffsetContainerRef.current != null) {
		centerOffsetContainerRef.current.addChild(sprite);
	}
	
	const _update = (delta:number) => {
		//console.log(`update called for ${id}`);
		let destroySprite = false;
		if(sprite) {
			if(sprite.getGlobalPosition().x < 0 - FBRConstants.SCREEN_BOUNDARY_MARGIN) {
				//console.log('tile exited on left side');
				if(fizzBuzzDetermination == 'fizz') {
					globalState.current.maxSequentialCorrectActions++;
					globalState.current.maxSequentialIncorrectActions = 0;
					globalState.current.totalCorrectActions++;
					incrementScore(5, globalState);
					if(tileValue == 69) {
						createTextChoiceResult(
							{ 
								textContent: 'Nice.',
								pixiTextConfig: scoreChangePixiTextOptions,
							}, 
							35, 
							sprite.getGlobalPosition().y, 
							-1,
							pixiAppRef,
							centerOffsetContainerRef,
							indicatorsRef,
						);
						showNoiceVideoFunc()
					} else {
						createTextChoiceResult(
							{ 
								textContent: '+5',
								pixiTextConfig: scoreChangePixiTextOptions,
							}, 
							35, 
							sprite.getGlobalPosition().y,
							-1,
							pixiAppRef,
							centerOffsetContainerRef,
							indicatorsRef,
						);
					}
				} else {
					globalState.current.maxSequentialCorrectActions = 0;
					globalState.current.maxSequentialIncorrectActions++;
					globalState.current.totalIncorrectActions++;
					incrementScore(-5, globalState);
					createTextChoiceResult( 
						{ 
							textContent: '-5',
							pixiTextConfig: scoreChangePixiTextOptions,
						}, 
						35,
						sprite.getGlobalPosition().y,
						-1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
				}
				destroySprite = true;
			} else if(sprite.getGlobalPosition().x > FBRConstants.STAGE_WIDTH + FBRConstants.SCREEN_BOUNDARY_MARGIN) {
				//console.log('tile exited on right side');
				if(fizzBuzzDetermination == 'buzz') {
					globalState.current.maxSequentialCorrectActions++;
					globalState.current.maxSequentialIncorrectActions = 0;
					globalState.current.totalCorrectActions++;
					incrementScore(5, globalState);
					createTextChoiceResult(
						{ 
							textContent: '+5',
							pixiTextConfig: scoreChangePixiTextOptions,
						}, 
						FBRConstants.STAGE_WIDTH - 35,
						sprite.getGlobalPosition().y,
						-1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
				} else {
					globalState.current.maxSequentialCorrectActions = 0;
					globalState.current.maxSequentialIncorrectActions++;
					globalState.current.totalIncorrectActions++;
					incrementScore(-5, globalState);
					createTextChoiceResult(
						{ 
							textContent: '-5',
							pixiTextConfig: scoreChangePixiTextOptions,
						}, 
						FBRConstants.STAGE_WIDTH - 35,
						sprite.getGlobalPosition().y,
						-1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
				}
				destroySprite = true;
			}	else if(state != 'initial' && sprite.getGlobalPosition().y < 0 - FBRConstants.SCREEN_BOUNDARY_MARGIN) {
				//console.log('tile exited on top');
				if(fizzBuzzDetermination == 'fizzbuzz') {
					globalState.current.maxSequentialCorrectActions++;
					globalState.current.maxSequentialIncorrectActions = 0;
					globalState.current.totalCorrectActions++;
					incrementScore(10, globalState);
					if(tileValue == 420) {
						createTextChoiceResult(
							{ 
								textContent: 'Nice.',
								pixiTextConfig: scoreChangePixiTextOptions,
							}, 
							sprite.getGlobalPosition().x, 
							35, 
							1,
							pixiAppRef,
							centerOffsetContainerRef,
							indicatorsRef,
						);
						showNoiceVideoFunc()
					} else {
						createTextChoiceResult(
							{ 
								textContent: '+10',
								pixiTextConfig: scoreChangePixiTextOptions,
							}, 
							sprite.getGlobalPosition().x, 
							35, 
							1,
							pixiAppRef,
							centerOffsetContainerRef,
							indicatorsRef,
						);
					}
				} else {
					globalState.current.maxSequentialCorrectActions = 0;
					globalState.current.maxSequentialIncorrectActions++;
					globalState.current.totalIncorrectActions++;
					incrementScore(-10, globalState);
					createTextChoiceResult(
						{ 
							textContent: '-10',
							pixiTextConfig: scoreChangePixiTextOptions,
						}, 
						sprite.getGlobalPosition().x,
						35,
						1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
				}
				destroySprite = true;
			}	else if(sprite.getGlobalPosition().y > FBRConstants.STAGE_HEIGHT + FBRConstants.SCREEN_BOUNDARY_MARGIN) {
				//console.log('tile exited on bottom');
				if(fizzBuzzDetermination == '') {
					//createIconChoiceResult(bottomCorrectIndicatorIconTexture, sprite.getGlobalPosition().x, FBRConstants.STAGE_HEIGHT+10, -1);
					globalState.current.maxSequentialCorrectActions++;
					globalState.current.maxSequentialIncorrectActions = 0;
					globalState.current.totalCorrectActions++;
					createIconChoiceResult(
						bottomCorrectIndicatorIconTexture,
						sprite.getGlobalPosition().x,
						FBRConstants.STAGE_HEIGHT+10,
						-1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
				} else {
					globalState.current.maxSequentialCorrectActions = 0;
					globalState.current.maxSequentialIncorrectActions++;
					globalState.current.totalIncorrectActions++;
					incrementScore(-5, globalState);
					createIconChoiceResult(
						bottomIncorrectIndicatorIconTexture,
						sprite.getGlobalPosition().x,
						FBRConstants.STAGE_HEIGHT+10,
						-1,
						pixiAppRef,
						centerOffsetContainerRef,
						indicatorsRef,
					);
					/*createTextChoiceResult(
							{ 
								textContent: '-5',
								pixiTextConfig: scoreChangePixiTextOptions,
							}, 
						sprite.getGlobalPosition().x, FBRConstants.STAGE_HEIGHT-30, -1);*/
				}
				destroySprite = true;
			}
		}
		if(state == 'destroy') {
			destroySprite = true;
		}
		if(destroySprite) {
			sprite.destroy();
			if(tilesRef.current != null) {
				delete tilesRef.current[id];
			}
		}
		if(!destroySprite) {
			if(state == 'initial') {
				sprite.y += ySpeed*delta;
			} else if (state == 'flicked') {
				sprite.x += xSpeed*delta;
				sprite.y += ySpeed*delta;
			}
		}
	};
	const _setState = (newState:string) => {
		//console.log(`setState() called for ${id}, newState = ${newState}`);
		state = newState;
	}
	const _setSpeed = (newXSpeed:number|null, newYSpeed:number|null) => {
		if(newXSpeed != null) {
			xSpeed = newXSpeed;
		}
		if(newYSpeed != null) {
			ySpeed = newYSpeed;
		}
	}
	
	return {
		id: id,
		update: _update,
		setState: _setState,
		setSpeed: _setSpeed,
	}
};

export default createTile;
